import React, { useState, useEffect, createContext, useContext, useCallback, useRef, useMemo, useReducer } from 'react'
import { supabase } from '../lib/supabase'
import db from '../lib/database'
import { estimateFieldsFromMove, clientFieldsFromMove, MOVE_MIRROR_CLIENT } from '../lib/moves'
import { kindSegments, kindIsPackOnly } from '../lib/phaseKinds'
import { setDayFlagsRegistry } from '../components/ui/dayFlags'
import { runMigrations, ensureSystemTables } from '../lib/migrations'
import { saveStoreSnapshot, loadStoreSnapshot } from '../lib/storeSnapshot'

export const StoreContext = createContext()
export const NavContext = createContext()

export function StoreProvider({ children }) {
  const [crew, setCrew] = useState([])
  const [trucks, setTrucks] = useState([])
  const [clients, setClients] = useState([])
  const [jobs, setJobs] = useState([])
  const [estimates, setEstimates] = useState([])
  // Moves (sql/094, moves-architecture.md): the Date / Estimate / Job-type
  // cluster. day_flags = Full / Closed days; truck_downtime = trucks out.
  const [moves, setMoves] = useState([])
  const [dayFlags, setDayFlags] = useState([])
  const [truckDowntime, setTruckDowntime] = useState([])
  useEffect(() => { setDayFlagsRegistry(dayFlags) }, [dayFlags])
  const [availability, setAvailability] = useState([])
  const [config, setConfig] = useState({})
  // Light directory of user_profiles — name + role + user_id. Loaded
  // alongside the rest of the store so the estimate-assignee dropdown
  // (and any future "@mention" / "owned by" surfaces) can render without
  // its own fetch round-trip. user_profiles.RLS already allows every
  // authenticated user to SELECT, so this is safe to share globally.
  const [userProfiles, setUserProfiles] = useState([])
  // Storage units (sql/084) — loaded with the store so the Storage page
  // and the client-file banner render without their own fetches.
  const [storageUnits, setStorageUnits] = useState([])          // sql/086: the ROOMS (inventory)
  const [storageFacilities, setStorageFacilities] = useState([])
  const [storageTenancies, setStorageTenancies] = useState([])  // who is in them
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Non-null (ISO timestamp of the snapshot) while the store is running
  // on last-good OFFLINE data instead of a live Supabase load — see the
  // hydrate-from-snapshot path in loadAll. Cleared the moment a live
  // fetch succeeds. Surfaces as the amber offline banner in the
  // estimator app (2026-08-26 offline pass).
  const [offlineSnapshotAt, setOfflineSnapshotAt] = useState(null)
  // Retry-with-backoff bookkeeping for the initial loadAll. The UI
  // (ErrorScreen) reads `retryInfo` to show "retrying in 4s, attempt
  // 3 of 5" copy so the user knows the app is healing itself.
  // null = no retry in flight. After max attempts we give up and let
  // `error` carry the final message.
  const [retryInfo, setRetryInfo] = useState(null)
  const retryTimerRef = useRef(null)

  // Undo/Redo history — stores last 20 actions for jobs and availability
  const [undoStack, setUndoStack] = useState([])
  const [redoStack, setRedoStack] = useState([])
  const MAX_UNDO = 20

  const pushUndo = useCallback((action) => {
    setUndoStack(prev => [...prev.slice(-(MAX_UNDO - 1)), { ...action, timestamp: Date.now() }])
    setRedoStack([]) // Clear redo when a new action is performed
  }, [])

  // Helper to execute an action (used by both undo and redo)
  const executeAction = async (action, isRedo) => {
    try {
      if (action.type === 'job_update') {
        const current = jobs.find(j => j.id === action.previousData.id)
        const reverted = await db.upsertJob(action.previousData)
        setJobs(prev => prev.map(j => j.id === reverted.id ? reverted : j))
        return { msg: isRedo ? 'Redid job change' : 'Reverted job change', reverseAction: { ...action, previousData: current ? { ...current } : action.previousData } }
      } else if (action.type === 'job_create') {
        const existing = jobs.find(j => j.id === action.jobId)
        await db.deleteJob(action.jobId)
        setJobs(prev => prev.filter(j => j.id !== action.jobId))
        return { msg: 'Removed newly created job', reverseAction: { type: 'job_delete', previousData: existing ? { ...existing } : null } }
      } else if (action.type === 'job_delete') {
        const restored = await db.upsertJob(action.previousData)
        setJobs(prev => [...prev, restored])
        return { msg: 'Restored deleted job', reverseAction: { type: 'job_create', jobId: restored.id } }
      } else if (action.type === 'availability_set') {
        const current = availability.find(a => a.crew_id === action.crewId && a.date === action.date)
        if (action.previousStatus) {
          const saved = await db.setAvailability(action.crewId, action.date, action.previousStatus, action.previousNote || '')
          setAvailability(prev => prev.map(a => (a.crew_id === action.crewId && a.date === action.date) ? saved : a))
        } else {
          await db.setAvailability(action.crewId, action.date, null)
          setAvailability(prev => prev.filter(a => !(a.crew_id === action.crewId && a.date === action.date)))
        }
        return { msg: 'Reverted availability', reverseAction: { ...action, previousStatus: current?.status || null, previousNote: current?.note || '' } }
      } else if (action.type === 'availability_clear') {
        const saved = await db.setAvailability(action.crewId, action.date, action.previousStatus, action.previousNote || '')
        setAvailability(prev => [...prev, saved])
        return { msg: 'Restored availability', reverseAction: { type: 'availability_set', crewId: action.crewId, date: action.date, previousStatus: null, previousNote: '' } }
      }
    } catch (err) {
      console.error('Action failed:', err)
      return { msg: `Failed: ${err.message}`, reverseAction: null }
    }
    return { msg: null, reverseAction: null }
  }

  const undo = useCallback(async () => {
    if (undoStack.length === 0) return null
    const lastAction = undoStack[undoStack.length - 1]
    setUndoStack(prev => prev.slice(0, -1))
    const result = await executeAction(lastAction, false)
    if (result.reverseAction) {
      setRedoStack(prev => [...prev.slice(-(MAX_UNDO - 1)), { ...result.reverseAction, timestamp: Date.now() }])
    }
    return result.msg
  }, [undoStack, jobs, availability])

  const redo = useCallback(async () => {
    if (redoStack.length === 0) return null
    const lastRedo = redoStack[redoStack.length - 1]
    setRedoStack(prev => prev.slice(0, -1))
    const result = await executeAction(lastRedo, true)
    if (result.reverseAction) {
      setUndoStack(prev => [...prev.slice(-(MAX_UNDO - 1)), { ...result.reverseAction, timestamp: Date.now() }])
    }
    return result.msg
  }, [redoStack, jobs, availability])

  // Backoff schedule for the initial loadAll. After each failure we wait
  // this many ms before the next attempt. If the user is still seeing the
  // error after 30s+8s+4s+2s+1s ≈ 45 seconds of cumulative retries, we
  // give up and surface the error permanently — they can hit "Retry"
  // manually from then on.
  const LOAD_RETRY_DELAYS_MS = useMemo(() => [1000, 2000, 4000, 8000, 30000], [])

  // Internal fetcher — does the actual data load without any retry
  // wrapping. Returns true on success, throws on failure.
  const fetchAll = useCallback(async () => {
    await runMigrations()
    const [crewData, truckData, clientData, jobData, configData, availData, estimateData, userProfileData, storageData, facilityData, tenancyData, moveData, flagData, downtimeData] = await Promise.all([
      db.getCrew(), db.getTrucks(), db.getClients(), db.getAllJobs(), db.getConfig(), db.getAvailability().catch(() => []), db.getEstimates().catch(() => []), db.getUserProfiles().catch(() => []),
      db.getStorageUnits().catch(() => []), db.getStorageFacilities().catch(() => []), db.getStorageTenancies().catch(() => []),
      db.getMoves().catch(() => []), db.getDayFlags().catch(() => []), db.getTruckDowntime().catch(() => []),
    ])
    setMoves(moveData)
    setDayFlags(flagData)
    setTruckDowntime(downtimeData)
    setCrew(crewData)
    setTrucks(truckData)
    setClients(clientData)
    setJobs(jobData)
    setConfig(configData)
    setAvailability(availData)
    setEstimates(estimateData)
    setUserProfiles(userProfileData)
    setStorageUnits(storageData)
    setStorageFacilities(facilityData)
    setStorageTenancies(tenancyData)
    // Live load succeeded — leave snapshot mode (if we were in it) and
    // persist this payload as the new last-good copy for the next
    // no-signal boot. Fire-and-forget: a snapshot failure never touches
    // the live app.
    setOfflineSnapshotAt(null)
    saveStoreSnapshot({
      crew: crewData, trucks: truckData, clients: clientData, jobs: jobData,
      config: configData, availability: availData, estimates: estimateData,
      userProfiles: userProfileData, storageUnits: storageData,
      storageFacilities: facilityData, storageTenancies: tenancyData,
      moves: moveData, dayFlags: flagData, truckDowntime: downtimeData,
    }).catch(() => {})
  }, [])

  // Hydrate the store from the last-good IndexedDB snapshot (offline
  // boot). Returns the snapshot timestamp on success, null when there's
  // nothing usable. Only called from loadAll's failure path.
  const hydrateFromSnapshot = useCallback(async () => {
    try {
      const snap = await loadStoreSnapshot()
      if (!snap || !Array.isArray(snap.clients)) return null
      setCrew(snap.crew || [])
      setTrucks(snap.trucks || [])
      setClients(snap.clients || [])
      setJobs(snap.jobs || [])
      setConfig(snap.config || {})
      setAvailability(snap.availability || [])
      setEstimates(snap.estimates || [])
      setUserProfiles(snap.userProfiles || [])
      setStorageUnits(snap.storageUnits || [])
      setStorageFacilities(snap.storageFacilities || [])
      setStorageTenancies(snap.storageTenancies || [])
      setMoves(snap.moves || [])
      setDayFlags(snap.dayFlags || [])
      setTruckDowntime(snap.truckDowntime || [])
      setOfflineSnapshotAt(snap.savedAt || 'unknown')
      return snap.savedAt || 'unknown'
    } catch (e) {
      console.warn('snapshot hydrate failed:', e?.message || e)
      return null
    }
  }, [])

  // Public loadAll — used both on initial mount and as the manual retry
  // handler on the error screen. Runs fetchAll, and on failure schedules
  // an automatic retry with exponential backoff, surfacing progress
  // through `retryInfo` so the UI can show a countdown.
  //
  // Calling loadAll() again from outside (e.g. the Retry button) cancels
  // any pending auto-retry timer and starts a fresh attempt cycle.
  const loadAll = useCallback(async () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
    setRetryInfo(null)

    // Snapshot-hydration bookkeeping for THIS retry cycle: try the
    // last-good offline snapshot once, on the first failure. If it
    // hydrates, the app becomes usable immediately (no error screen)
    // while the retries keep running quietly in the background — the
    // moment a live fetch succeeds it replaces the snapshot data.
    let snapshotTried = false
    let onSnapshot = false

    const attempt = async (n) => {
      try {
        if (!onSnapshot) setLoading(true)
        await fetchAll()
        setError(null)
        setRetryInfo(null)
        setLoading(false)
      } catch (err) {
        console.error(`Load attempt ${n + 1} failed:`, err)
        if (!snapshotTried) {
          snapshotTried = true
          const savedAt = await hydrateFromSnapshot()
          if (savedAt) {
            console.warn(`Offline boot — showing snapshot from ${savedAt}; retrying live load in background`)
            onSnapshot = true
          }
        }
        const isLastAttempt = n >= LOAD_RETRY_DELAYS_MS.length
        if (isLastAttempt) {
          // Out of retries. On snapshot: stay usable on last-good data,
          // no error screen (the offline banner already tells the story;
          // manual Retry still works from anywhere that calls loadAll).
          setError(onSnapshot ? null : (err?.message || 'Failed to load data'))
          setRetryInfo(null)
          setLoading(false)
          return
        }
        const delay = LOAD_RETRY_DELAYS_MS[n]
        // Surface to UI so the error screen shows "Retrying in 4s,
        // attempt 3 of 5..." with a live countdown. nextAttemptAt is
        // a wall-clock timestamp so the countdown can render correctly
        // even if React renders on a different cadence. On snapshot the
        // app is already interactive — no error screen, quiet retries.
        setError(onSnapshot ? null : (err?.message || 'Failed to load data'))
        setRetryInfo(onSnapshot ? null : {
          attempt: n + 1,
          maxAttempts: LOAD_RETRY_DELAYS_MS.length + 1,
          nextAttemptAt: Date.now() + delay,
        })
        setLoading(false)
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null
          attempt(n + 1)
        }, delay)
      }
    }

    attempt(0)
  }, [fetchAll, hydrateFromSnapshot, LOAD_RETRY_DELAYS_MS])

  useEffect(() => {
    loadAll()
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }
  }, [loadAll])

  // ===== REALTIME SUBSCRIPTIONS — sync across all users =====
  //
  // Gated on an authenticated session (QA M2/M3 observation: repeated
  // "[Realtime] CHANNEL_ERROR"). The old mount-time subscribe joined the
  // channel BEFORE login completed; with RLS on every subscribed table,
  // Realtime rejects unauthenticated postgres_changes joins — so the
  // channel error-looped and staff could quietly run without live
  // updates.
  //
  // 2026-09-09 (Better Stack timeouts / Disk IO investigation): this
  // effect used to be keyed on the ACCESS TOKEN, so every hourly refresh
  // — in every open tab — tore the channel down and rebuilt it: 12
  // subscription rows deleted + re-inserted in realtime.subscription and
  // the RLS-check query re-run for each. pg_stat showed 180k inserts /
  // 180k deletes and 2,674 autovacuums on that one table, plus ~200k
  // subscription-check queries. Now: the channel is keyed on the USER
  // (join once per login) and a fresh JWT is pushed to the live socket
  // with realtime.setAuth — the supported way to keep a joined channel
  // authenticated across refreshes. Per-table INSERT/UPDATE/DELETE
  // triples also collapsed to one '*' listener each (15 → 9 rows per join).
  const [rtSession, setRtSession] = useState(null)
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setRtSession(data?.session ?? null))
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => setRtSession(session ?? null))
    return () => sub?.subscription?.unsubscribe()
  }, [])

  // Keep the live socket's JWT fresh WITHOUT rejoining (hourly refresh).
  useEffect(() => {
    if (rtSession?.access_token) supabase.realtime.setAuth(rtSession.access_token)
  }, [rtSession?.access_token])

  const rtUserId = rtSession?.user?.id || null
  useEffect(() => {
    if (!rtUserId || !rtSession?.access_token) return
    supabase.realtime.setAuth(rtSession.access_token)
    // Generic row sync: INSERT → prepend/append if unseen, UPDATE → merge, DELETE → drop.
    const sync = (setter, { append = false } = {}) => (payload) => {
      if (payload.eventType === 'DELETE') setter(prev => prev.filter(r => r.id !== payload.old.id))
      else if (payload.eventType === 'INSERT') setter(prev => prev.some(r => r.id === payload.new.id) ? prev : (append ? [...prev, payload.new] : [payload.new, ...prev]))
      else setter(prev => prev.map(r => r.id === payload.new.id ? { ...r, ...payload.new } : r))
    }
    const channel = supabase.channel('bbm-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'clients' }, sync(setClients))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'move_jobs' }, sync(setJobs, { append: true }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'estimates' }, sync(setEstimates))
      // MOVES (sql/094) — the DB mirror writes options + client rows from
      // triggers, and those arrive through the clients/estimates channels
      // above; the move itself arrives here.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'moves' }, sync(setMoves))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'day_flags' }, (payload) => {
        if (payload.eventType === 'DELETE') setDayFlags(prev => prev.filter(f => f.date !== payload.old.date))
        else setDayFlags(prev => prev.some(f => f.date === payload.new.date) ? prev.map(f => f.date === payload.new.date ? payload.new : f) : [...prev, payload.new])
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'truck_downtime' }, sync(setTruckDowntime, { append: true }))
      // CREW MEMBERS
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crew_members' }, (payload) => {
        if (payload.eventType === 'DELETE') {
          setCrew(prev => prev.filter(c => c.id !== payload.old.id))
        } else {
          setCrew(prev => {
            const exists = prev.find(c => c.id === payload.new.id)
            return exists ? prev.map(c => c.id === payload.new.id ? { ...c, ...payload.new } : c) : [...prev, payload.new]
          })
        }
      })
      // TRUCKS
      .on('postgres_changes', { event: '*', schema: 'public', table: 'trucks' }, (payload) => {
        if (payload.eventType === 'DELETE') {
          setTrucks(prev => prev.filter(t => t.id !== payload.old.id))
        } else {
          setTrucks(prev => {
            const exists = prev.find(t => t.id === payload.new.id)
            return exists ? prev.map(t => t.id === payload.new.id ? { ...t, ...payload.new } : t) : [...prev, payload.new]
          })
        }
      })
      // AVAILABILITY
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crew_availability' }, (payload) => {
        if (payload.eventType === 'DELETE') {
          setAvailability(prev => prev.filter(a => a.id !== payload.old.id))
        } else {
          setAvailability(prev => {
            const exists = prev.find(a => a.id === payload.new.id)
            return exists ? prev.map(a => a.id === payload.new.id ? { ...a, ...payload.new } : a) : [...prev, payload.new]
          })
        }
      })
      // STORAGE v2 (sql/086) — tenancies flip quoted→active from a DB
      // trigger, so the page must hear it; units/facilities for office
      // mates editing Settings → Storage.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'storage_tenancies' }, (payload) => {
        if (payload.eventType === 'DELETE') setStorageTenancies(prev => prev.filter(t => t.id !== payload.old.id))
        else setStorageTenancies(prev => prev.some(t => t.id === payload.new.id) ? prev.map(t => t.id === payload.new.id ? { ...t, ...payload.new } : t) : [payload.new, ...prev])
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'storage_units' }, (payload) => {
        if (payload.eventType === 'DELETE') setStorageUnits(prev => prev.filter(u => u.id !== payload.old.id))
        else setStorageUnits(prev => (prev.some(u => u.id === payload.new.id) ? prev.map(u => u.id === payload.new.id ? { ...u, ...payload.new } : u) : [...prev, payload.new])
          .sort((a, b) => (a.sort_order - b.sort_order) || String(a.unit_number).localeCompare(String(b.unit_number))))
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'storage_facilities' }, (payload) => {
        if (payload.eventType === 'DELETE') setStorageFacilities(prev => prev.filter(f => f.id !== payload.old.id))
        else setStorageFacilities(prev => prev.some(f => f.id === payload.new.id) ? prev.map(f => f.id === payload.new.id ? { ...f, ...payload.new } : f) : [...prev, payload.new])
      })
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') console.log('[Realtime] Live')
        else console.warn('[Realtime] Subscription status:', status, err?.message || '')
      })

    return () => { supabase.removeChannel(channel) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtUserId])

  const saveClient = useCallback(async (client) => {
    const saved = await db.upsertClient(client)
    setClients(prev => {
      const exists = prev.find(c => c.id === saved.id)
      return exists ? prev.map(c => c.id === saved.id ? saved : c) : [saved, ...prev]
    })
    // A new client's lead move was created by the DB (clients_ensure_move_trg).
    if (!client.id && saved.current_move_id) {
      try {
        const mv = await db.getMove(saved.current_move_id)
        if (mv) setMoves(prev => prev.some(m => m.id === mv.id) ? prev.map(m => m.id === mv.id ? mv : m) : [mv, ...prev])
      } catch (e) { console.warn('new client move fetch failed:', e?.message || e) }
    }
    return saved
  }, [])

  // ── Moves (sql/094) ────────────────────────────────────────────────────
  // Apply the DB mirror in memory too, so the UI doesn't wait for realtime:
  // every option of the move and (if current) the client get the move's
  // identity + dates.
  const applyMoveMirrorLocally = useCallback((mv) => {
    if (!mv) return
    const eFields = estimateFieldsFromMove(mv)
    setEstimates(prev => prev.map(e => e.move_id === mv.id ? { ...e, ...eFields } : e))
    const cFields = clientFieldsFromMove(mv)
    setClients(prev => prev.map(c => (c.id === mv.client_id && c.current_move_id === mv.id) ? { ...c, ...cFields } : c))
  }, [])

  // Who is acting, for the date-confirmation log (2026-09-18).
  const actorName = useCallback(() => {
    const u = rtSession?.user
    const prof = u?.id ? userProfiles.find(p => p.user_id === u.id) : null
    return prof?.display_name || prof?.name || u?.user_metadata?.full_name || u?.user_metadata?.name || u?.email || null
  }, [rtSession, userProfiles])

  const saveMove = useCallback(async (move) => {
    // Flipping a date's key from Tentative → Confirmed (TripStrip) is a
    // confirmation too: append it to the log so "who confirmed, when" is
    // answered the same way as a Book-time or Confirm-all confirmation.
    const prev = move?.id ? moves.find(m => m.id === move.id) : null
    let patch = move
    if (prev) {
      const now = new Date().toISOString()
      const by = actorName()
      const log = Array.isArray(prev.date_confirmations) ? [...prev.date_confirmations] : []
      let logged = false
      for (const k of ['load', 'unload', 'pack']) {
        const tk = `${k}_tentative`
        const date = (`${k}_date` in move) ? move[`${k}_date`] : prev[`${k}_date`]
        if (tk in move && move[tk] === false && prev[tk] === true && date) { log.push({ key: k, date, at: now, by }); logged = true }
      }
      if (logged) patch = { ...move, date_confirmations: log, dates_confirmed_at: now, dates_confirmed_by: by }
    }
    const saved = await db.upsertMove(patch)
    setMoves(prev => prev.some(m => m.id === saved.id) ? prev.map(m => m.id === saved.id ? saved : m) : [saved, ...prev])
    applyMoveMirrorLocally(saved)
    return saved
  }, [applyMoveMirrorLocally, moves, actorName])

  const createMove = useCallback(async (clientId, fields = {}, opts = {}) => {
    const mv = await db.createMove(clientId, fields, opts)
    setMoves(prev => [mv, ...prev.filter(m => m.id !== mv.id)])
    if (opts.makeCurrent !== false) setClients(prev => prev.map(c => c.id === clientId ? { ...c, current_move_id: mv.id, ...clientFieldsFromMove(mv) } : c))
    return mv
  }, [])

  // Remove a bare move (see db.removeMove); the newest remaining move
  // becomes current and its fields land on the client row in memory.
  const removeMove = useCallback(async (moveId) => {
    const res = await db.removeMove(moveId)
    setMoves(prev => prev.filter(m => m.id !== moveId))
    if (res?.client) {
      const cur = moves.find(m => m.id === res.currentId)
      setClients(prev => prev.map(c => c.id === res.client.id ? { ...c, current_move_id: res.currentId, ...(cur ? clientFieldsFromMove(cur) : {}) } : c))
    }
    return res
  }, [moves])

  // Delete a client outright (see db.deleteClient for the guards). Drops
  // the client, its moves, quotes and legs from memory — realtime would
  // catch up, but the slide-over closes on the spot.
  const deleteClient = useCallback(async (clientId) => {
    const res = await db.deleteClient(clientId)
    setClients(prev => prev.filter(c => c.id !== clientId))
    setMoves(prev => prev.filter(m => m.client_id !== clientId))
    setEstimates(prev => prev.filter(e => e.client_id !== clientId))
    setJobs(prev => prev.filter(j => j.client_id !== clientId))
    return res
  }, [])

  const setCurrentMove = useCallback(async (clientId, moveId) => {
    const saved = await db.setCurrentMove(clientId, moveId)
    const mv = moves.find(m => m.id === moveId)
    setClients(prev => prev.map(c => c.id === saved.id ? { ...saved, ...(mv ? clientFieldsFromMove(mv) : {}) } : c))
    return saved
  }, [moves])

  const confirmMoveDates = useCallback(async (moveId, keys, by) => {
    const saved = await db.confirmMoveDates(moveId, keys, by)
    setMoves(prev => prev.map(m => m.id === saved.id ? saved : m))
    applyMoveMirrorLocally(saved)
    return saved
  }, [applyMoveMirrorLocally])

  const shiftMoveDates = useCallback(async (moveId, newLoadDate, opts = {}) => {
    const res = await db.shiftMoveDates(moveId, newLoadDate, opts)
    if (res?.move) { setMoves(prev => prev.map(m => m.id === res.move.id ? res.move : m)); applyMoveMirrorLocally(res.move) }
    if (res?.shiftedJobs?.length) setJobs(prev => prev.map(j => res.shiftedJobs.find(x => x.id === j.id) || j))
    return res
  }, [applyMoveMirrorLocally])

  // Re-read a move + its options + its client after a status change the
  // DB reacted to (booking declines siblings, flips the current move).
  const refreshMove = useCallback(async (moveId) => {
    if (!moveId) return
    try {
      const mv = await db.getMove(moveId)
      if (!mv) return
      setMoves(prev => prev.some(m => m.id === mv.id) ? prev.map(m => m.id === mv.id ? mv : m) : [mv, ...prev])
      const { data: opts } = await supabase.from('estimates').select('*').eq('move_id', moveId)
      if (opts) setEstimates(prev => {
        const merged = prev.map(e => opts.find(o => o.id === e.id) ? { ...e, ...opts.find(o => o.id === e.id) } : e)
        const unseen = opts.filter(o => !prev.some(e => e.id === o.id))
        return unseen.length ? [...unseen, ...merged] : merged
      })
      const { data: cl } = await supabase.from('clients').select('*').eq('id', mv.client_id).maybeSingle()
      if (cl) setClients(prev => prev.map(c => c.id === cl.id ? { ...c, ...cl } : c))
    } catch (e) { console.warn('refreshMove failed:', e?.message || e) }
  }, [])

  const saveDayFlag = useCallback(async (date, kind, note, by) => {
    const row = await db.setDayFlag(date, kind, note, by)
    setDayFlags(prev => { const rest = prev.filter(f => f.date !== date); return row ? [...rest, row].sort((a, b) => a.date.localeCompare(b.date)) : rest })
    return row
  }, [])
  const saveTruckDowntime = useCallback(async (row) => {
    const saved = await db.upsertTruckDowntime(row)
    setTruckDowntime(prev => prev.some(d => d.id === saved.id) ? prev.map(d => d.id === saved.id ? saved : d) : [...prev, saved])
    return saved
  }, [])
  const removeTruckDowntime = useCallback(async (id) => {
    await db.deleteTruckDowntime(id)
    setTruckDowntime(prev => prev.filter(d => d.id !== id))
  }, [])

  // Partial patch of an existing client (targeted UPDATE, no NOT NULL insert
  // path) — used where only a few fields change, e.g. walkthrough status.
  const patchClient = useCallback(async (id, patch) => {
    const saved = await db.patchClient(id, patch)
    setClients(prev => prev.map(c => c.id === saved.id ? saved : c))
    // Moves (sql/094): db.patchClient redirects date/address keys to the
    // client's current move — pull that move back so the switcher/strip
    // don't wait for realtime.
    if (saved.current_move_id && Object.keys(patch || {}).some(k => MOVE_MIRROR_CLIENT.includes(k) || /^stop_\d+_/.test(k))) {
      try { const mv = await db.getMove(saved.current_move_id); if (mv) setMoves(prev => prev.some(m => m.id === mv.id) ? prev.map(m => m.id === mv.id ? mv : m) : [mv, ...prev]) } catch { /* realtime will catch up */ }
    }
    return saved
  }, [])

  const saveCrew = useCallback(async (member) => {
    const saved = await db.upsertCrew(member)
    setCrew(prev => {
      const exists = prev.find(c => c.id === saved.id)
      return exists ? prev.map(c => c.id === saved.id ? saved : c) : [...prev, saved]
    })
    return saved
  }, [])

  const saveTruck = useCallback(async (truck) => {
    const saved = await db.upsertTruck(truck)
    setTrucks(prev => {
      const exists = prev.find(t => t.id === saved.id)
      return exists ? prev.map(t => t.id === saved.id ? saved : t) : [...prev, saved]
    })
    return saved
  }, [])

  // Crew-swap notice state — see the detection block inside saveJob.
  const [swapNotice, setSwapNotice] = useState(null)
  const clearSwapNotice = useCallback(() => setSwapNotice(null), [])

  const saveJob = useCallback(async (job) => {
    // Push previous state to undo stack
    const previousJob = job.id ? jobs.find(j => j.id === job.id) : null
    if (previousJob) pushUndo({ type: 'job_update', previousData: { ...previousJob } })
    const saved = await db.upsertJob(job)
    if (!job.id) {
      // New job — record creation for undo
      pushUndo({ type: 'job_create', jobId: saved.id })
    }
    setJobs(prev => {
      const exists = prev.find(j => j.id === saved.id)
      return exists ? prev.map(j => j.id === saved.id ? saved : j) : [...prev, saved]
    })

    // Auto-transition the parent estimate from 'booked' -> 'completed'
    // when the FINAL PHASE of the move is ops-completed (Jaylen's rule,
    // 2026-07-07, QA Module-2 BUG-1). "Final phase" = the job with the
    // highest phase_sequence on the estimate (ties/no-sequence count as
    // final). Earlier phases completing don't trigger it, but stragglers
    // — e.g. a PACK day that was never assigned — don't BLOCK it either:
    // once the truck is unloaded and billed, the sales pipeline is done.
    // (Replaces the old all-jobs-terminal rule, which stuck estimates at
    // 'booked' forever when any sibling row never got dispatched.)
    // Only fires on the booked -> completed step so we don't clobber
    // 'cancelled' estimates or stomp manual edits.
    const justCompleted =
      saved.status === 'completed' &&
      previousJob?.status !== 'completed' &&
      saved.estimate_id
    if (justCompleted) {
      try {
        const siblings = jobs.filter(j =>
          j.estimate_id === saved.estimate_id && j.id !== saved.id && !j._isSpanDay)
        const seqOf = j => (Number.isFinite(+j?.phase_sequence) && j?.phase_sequence !== null) ? +j.phase_sequence : 0
        const isFinalPhase = siblings.every(j => seqOf(j) <= seqOf(saved))
        if (isFinalPhase) {
          const parent = estimates.find(e => e.id === saved.estimate_id)
          if (parent && parent.status === 'booked') {
            const updated = await db.upsertEstimate({ ...parent, status: 'completed' })
            setEstimates(prev => prev.map(e => e.id === updated.id ? updated : e))
          }
        }
      } catch (e) {
        // Non-fatal — the job save already succeeded. The estimate can
        // be flipped manually if this fails (e.g. network blip).
        console.warn('Auto-transition estimate booked -> completed failed:', e)
      }
    }

    // ── Crew-swap notice (Jaylen 2026-07-08) ────────────────────────────
    // If the crew changed on a LIVE job (dispatched / in_progress — i.e.
    // the crew has already been told about it), queue a prompt so the
    // dispatcher can text the affected LEADS. Confirm-first by design: no
    // SMS ever fires without the dispatcher saying so (dialog host is
    // <CrewSwapNotice/> in App.jsx). Assigned-but-not-dispatched shuffling
    // stays silent — that's normal planning. Rapid multi-phase operations
    // overwrite the notice with the latest change (v1: one prompt, not a
    // queue).
    try {
      const LIVE_STATUSES = ['dispatched', 'in_progress']
      if (
        previousJob && saved.type !== 'WALKTHROUGH' &&
        (LIVE_STATUSES.includes(previousJob.status) || LIVE_STATUSES.includes(saved.status))
      ) {
        const prevCrewIds = new Set(previousJob.crew_ids || [])
        const newCrewIds = new Set(saved.crew_ids || [])
        const crewChanged =
          previousJob.leader_id !== saved.leader_id ||
          prevCrewIds.size !== newCrewIds.size ||
          [...prevCrewIds].some(id => !newCrewIds.has(id))
        if (crewChanged) {
          setSwapNotice({
            job: saved,
            prevLeaderId: previousJob.leader_id || null,
            newLeaderId: saved.leader_id || null,
            addedIds: [...newCrewIds].filter(id => !prevCrewIds.has(id)),
            removedIds: [...prevCrewIds].filter(id => !newCrewIds.has(id)),
          })
        }
      }
    } catch (e) { /* notice is best-effort — never block the save */ }

    // Keep the parent estimate's dates in sync with its dispatch jobs.
    // When a job's date changes on the board (drag/drop + confirm), the
    // estimate — and the client-file date list that reads it — should track
    // it. Only runs when the date actually changed, so crew/truck edits don't
    // touch the estimate. Status stays 'booked', so this never re-triggers the
    // phase push.
    //
    // TWO clocks, kept separate:
    //   • PACK phases  → estimates.pack_date. Packing usually happens BEFORE
    //                    the move, so a pack job is often the earliest job.
    //                    It must NOT feed the move_date anchor (that would let
    //                    an early pack day silently rewrite the move date).
    //   • everything else → estimates.move_date, anchored to the earliest
    //                    NON-pack job date.
    if (saved.estimate_id && saved.move_date && previousJob && previousJob.move_date !== saved.move_date) {
      try {
        // Moves (sql/094): the board writes the MOVE; the DB mirror carries
        // it to the option rows and the client file — the dialog's "updates
        // the move date everywhere" is finally true.
        const parent = estimates.find(e => e.id === saved.estimate_id)
        const mv = moves.find(m => m.id === (saved.move_id || parent?.move_id))
        const segs = kindSegments(saved.phase_kind || 'move')
        const isPackJob = segs.length === 1 && segs[0] === 'pack'
        const isUnloadJob = !segs.includes('load') && !segs.includes('move') && segs.includes('unload')
        if (mv) {
          if (isPackJob) {
            if (mv.pack_date !== saved.move_date) await saveMove({ id: mv.id, pack_date: saved.move_date })
          } else if (isUnloadJob) {
            if (mv.unload_date && mv.unload_date !== saved.move_date) await saveMove({ id: mv.id, unload_date: saved.move_date, unload_window_end: null })
          } else {
            // Load-date anchor — earliest non-span, non-pack, non-unload-side job.
            const sibs = [
              ...jobs.filter(j => j.estimate_id === saved.estimate_id && j.id !== saved.id),
              saved,
            ].filter(j => j.move_date && !j._isSpanDay && j.type !== 'WALKTHROUGH' && !kindIsPackOnly(j.phase_kind) && (kindSegments(j.phase_kind || 'move').some(sg => ['load', 'move', 'trash_removal', 'delivery', 'pickup'].includes(sg))))
            const anchor = sibs.map(j => j.move_date).sort()[0] || saved.move_date
            if (mv.load_date !== anchor) await saveMove({ id: mv.id, load_date: anchor })
          }
        } else if (parent && parent.move_date !== saved.move_date && !isPackJob) {
          const updatedEst = await db.upsertEstimate({ ...parent, move_date: saved.move_date })
          setEstimates(prev => prev.map(e => e.id === updatedEst.id ? updatedEst : e))
        }
      } catch (e) {
        console.warn('estimate date sync from dispatch skipped:', e?.message || e)
      }
    }

    return saved
  }, [jobs, estimates, moves, saveMove, pushUndo])

  const removeJob = useCallback(async (id) => {
    const existing = jobs.find(j => j.id === id)
    if (existing) pushUndo({ type: 'job_delete', previousData: { ...existing } })
    await db.deleteJob(id)
    setJobs(prev => prev.filter(j => j.id !== id))
  }, [jobs, pushUndo])

  const deactivateCrew = useCallback(async (id) => {
    const member = crew.find(c => c.id === id)
    if (member) await db.upsertCrew({ ...member, active: false })
    setCrew(prev => prev.map(c => c.id === id ? { ...c, active: false } : c))
  }, [crew])

  const permanentlyDeleteCrew = useCallback(async (id) => {
    await db.deleteCrew(id)
    setCrew(prev => prev.filter(c => c.id !== id))
  }, [])

  const deactivateTruck = useCallback(async (id) => {
    const truck = trucks.find(t => t.id === id)
    if (truck) await db.upsertTruck({ ...truck, active: false })
    setTrucks(prev => prev.map(t => t.id === id ? { ...t, active: false } : t))
  }, [])

  const saveEstimate = useCallback(async (estimate) => {
    // Auto-generate job_number for new estimates
    if (!estimate.id && !estimate.job_number) {
      estimate.job_number = await db.getNextJobNumber()
    }
    const prevStatus = estimate.id ? (estimates.find(e => e.id === estimate.id)?.status ?? null) : null
    const saved = await db.upsertEstimate(estimate)
    setEstimates(prev => {
      const exists = prev.find(e => e.id === saved.id)
      return exists ? prev.map(e => e.id === saved.id ? saved : e) : [saved, ...prev]
    })
    // Moves (sql/094): a fresh option may have created/attached a move; a
    // booking declines its siblings and flips the client's current move.
    // Postponed (2026-09-21): db.upsertEstimate removed the option's
    // pending legs and flipped the move's dates to tentative.
    const postponedNow = !!estimate.id && saved.status === 'postponed' && prevStatus !== 'postponed'
    if (postponedNow) setJobs(prev => prev.filter(j => !(j.estimate_id === saved.id && !['in_progress', 'completed'].includes(j.status))))
    if (saved.move_id && (!estimate.id || saved.status === 'booked' || postponedNow || !moves.some(m => m.id === saved.move_id))) {
      refreshMove(saved.move_id)
    }
    return saved
  }, [moves, refreshMove, estimates])

  const removeEstimate = useCallback(async (id) => {
    const res = await db.deleteEstimate(id)
    const relettered = new Map((res?.relettered || []).map(e => [e.id, e]))
    setEstimates(prev => prev.filter(e => e.id !== id).map(e => relettered.get(e.id) || e))
    return res
  }, [])

  // Change an estimate's move date in place; optionally shift its booked
  // dispatch jobs by the same delta. Updates both estimate + job state so the
  // Estimates list and the dispatch board reflect the change without a reload.
  const shiftEstimateMoveDate = useCallback(async (estimateId, newDate, opts = {}) => {
    const res = await db.shiftEstimateMoveDate(estimateId, newDate, opts)
    if (res?.estimate) setEstimates(prev => prev.map(e => e.id === res.estimate.id ? res.estimate : e))
    if (res?.shiftedJobs?.length) {
      setJobs(prev => prev.map(j => res.shiftedJobs.find(s => s.id === j.id) || j))
    }
    return res
  }, [])

  // Crew note sets — fetched per-client on demand (not held in global state,
  // since they're only needed when a specific client/estimate is open).
  const getCrewNoteSets = useCallback((clientId) => db.getCrewNoteSets(clientId), [])
  const saveCrewNoteSet = useCallback((set) => db.saveCrewNoteSet(set), [])
  const deleteCrewNoteSet = useCallback((id) => db.deleteCrewNoteSet(id), [])
  const getInternalNoteSets = useCallback((clientId) => db.getInternalNoteSets(clientId), [])
  const saveInternalNoteSet = useCallback((set) => db.saveInternalNoteSet(set), [])
  const deleteInternalNoteSet = useCallback((id) => db.deleteInternalNoteSet(id), [])

  const saveConfig = useCallback(async (key, value) => {
    await db.setConfig(key, value)
    setConfig(prev => ({ ...prev, [key]: value }))
  }, [])

  const setCrewAvailability = useCallback(async (crewId, date, status, note) => {
    // Push previous state to undo stack
    const existing = availability.find(a => a.crew_id === crewId && a.date === date)
    if (!status && existing) {
      pushUndo({ type: 'availability_clear', crewId, date, previousStatus: existing.status, previousNote: existing.note })
    } else if (status) {
      pushUndo({ type: 'availability_set', crewId, date, previousStatus: existing?.status || null, previousNote: existing?.note || '' })
    }
    const saved = await db.setAvailability(crewId, date, status, note)
    if (!status) {
      setAvailability(prev => prev.filter(a => !(a.crew_id === crewId && a.date === date)))
    } else if (saved) {
      setAvailability(prev => {
        const exists = prev.find(a => a.crew_id === crewId && a.date === date)
        return exists ? prev.map(a => (a.crew_id === crewId && a.date === date) ? saved : a) : [...prev, saved]
      })
    }
    return saved
  }, [availability, pushUndo])

  // Auto-save daily snapshot whenever jobs or availability change (debounced)
  const snapshotTimerRef = useRef(null)
  useEffect(() => {
    if (loading || jobs.length === 0) return
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current)
    snapshotTimerRef.current = setTimeout(async () => {
      try {
        const { format } = await import('date-fns')
        const today = format(new Date(), 'yyyy-MM-dd')
        await db.saveSnapshot(today, jobs, availability)
        await db.deleteOldSnapshots(7)
      } catch (e) { console.log('Snapshot save note:', e.message) }
    }, 5000) // 5 second debounce
    return () => { if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current) }
  }, [jobs, availability, loading])

  // Restore from snapshot
  const restoreSnapshot = useCallback(async (snapshotDate) => {
    const snap = await db.getSnapshot(snapshotDate)
    if (!snap) throw new Error('Snapshot not found')
    // Restore jobs: delete all current, re-insert from snapshot
    const currentJobs = await db.getAllJobs()
    for (const j of currentJobs) { await db.deleteJob(j.id) }
    for (const j of snap.jobs_data) {
      const { id, created_at, ...rest } = j
      await db.upsertJob(rest)
    }
    // Restore availability
    const currentAvail = await db.getAvailability()
    for (const a of currentAvail) {
      await db.setAvailability(a.crew_id, a.date, null)
    }
    for (const a of snap.availability_data) {
      await db.setAvailability(a.crew_id, a.date, a.status, a.note)
    }
    // Reload everything
    await loadAll()
    return snap
  }, [loadAll])

  const getSnapshots = useCallback(async () => {
    try { return await db.getSnapshots(7) }
    catch (e) { console.log('Snapshots not available yet:', e.message); return [] }
  }, [])

  const reorderCrew = useCallback(async (orderedIds) => {
    try {
      for (let i = 0; i < orderedIds.length; i++) {
        await db.updateCrewSortOrder(orderedIds[i], i)
      }
      // Refresh from DB to get clean state
      const fresh = await db.getCrew()
      setCrew(fresh)
    } catch (e) {
      console.error('Reorder error:', e)
      throw e
    }
  }, [])

  // ── Storage v2 (sql/086) ──────────────────────────────────────────
  const upsertInto = (setter) => (saved) => setter(prev => {
    const exists = prev.find(u => u.id === saved.id)
    return exists ? prev.map(u => u.id === saved.id ? saved : u) : [saved, ...prev]
  })

  const saveStorageFacility = useCallback(async (f) => {
    const saved = await db.upsertStorageFacility(f)
    upsertInto(setStorageFacilities)(saved)
    return saved
  }, [])
  const removeStorageFacility = useCallback(async (id) => {
    await db.deleteStorageFacility(id)
    setStorageFacilities(prev => prev.filter(u => u.id !== id))
    setStorageUnits(prev => prev.filter(u => u.facility_id !== id))
  }, [])

  const saveStorageUnit = useCallback(async (unit) => {
    const saved = await db.upsertStorageUnit(unit)
    setStorageUnits(prev => {
      const exists = prev.find(u => u.id === saved.id)
      const next = exists ? prev.map(u => u.id === saved.id ? saved : u) : [...prev, saved]
      return next.sort((a, b) => (a.sort_order - b.sort_order) || String(a.unit_number).localeCompare(String(b.unit_number)))
    })
    return saved
  }, [])
  const patchStorageUnit = useCallback(async (id, patch) => {
    const saved = await db.patchStorageUnit(id, patch)
    setStorageUnits(prev => prev.map(u => u.id === saved.id ? saved : u))
    return saved
  }, [])
  const removeStorageUnit = useCallback(async (id) => {
    await db.deleteStorageUnit(id)
    setStorageUnits(prev => prev.filter(u => u.id !== id))
  }, [])

  // Tenancies: create via save, everything else via patch (FK-safe).
  const saveStorageTenancy = useCallback(async (t) => {
    const saved = await db.upsertStorageTenancy(t)
    upsertInto(setStorageTenancies)(saved)
    return saved
  }, [])
  const patchStorageTenancy = useCallback(async (id, patch) => {
    const saved = await db.patchStorageTenancy(id, patch)
    setStorageTenancies(prev => prev.map(u => u.id === saved.id ? saved : u))
    return saved
  }, [])
  const removeStorageTenancy = useCallback(async (id) => {
    await db.deleteStorageTenancy(id)
    setStorageTenancies(prev => prev.filter(u => u.id !== id))
  }, [])

  const value = { crew, trucks, clients, jobs, estimates, moves, dayFlags, truckDowntime, saveMove, createMove, setCurrentMove, removeMove, deleteClient, confirmMoveDates, shiftMoveDates, refreshMove, saveDayFlag, saveTruckDowntime, removeTruckDowntime, availability, config, userProfiles, storageUnits, saveStorageUnit, patchStorageUnit, removeStorageUnit, storageFacilities, saveStorageFacility, removeStorageFacility, storageTenancies, saveStorageTenancy, patchStorageTenancy, removeStorageTenancy, loading, error, retryInfo, offlineSnapshotAt, loadAll, saveClient, patchClient, saveCrew, saveTruck, saveJob, removeJob, saveEstimate, removeEstimate, shiftEstimateMoveDate, getCrewNoteSets, saveCrewNoteSet, deleteCrewNoteSet, getInternalNoteSets, saveInternalNoteSet, deleteInternalNoteSet, saveConfig, deactivateCrew, permanentlyDeleteCrew, deactivateTruck, setCrewAvailability, reorderCrew, undoStack, undo, redoStack, redo, restoreSnapshot, getSnapshots, swapNotice, clearSwapNotice }
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export function useStore() { return useContext(StoreContext) }

export function useNav() { return useContext(NavContext) }
